import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Pencil, Plus, Trash2, Save, X, GripVertical } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEditMode } from "@/contexts/EditModeContext";
import { Label } from "@/components/ui/label";

interface FeatureSection {
  headline: string;
  bullets: string[];
  imageUrl?: string;
}

interface EditableFeatureSectionsProps {
  sections: FeatureSection[];
  onSave: (sections: FeatureSection[]) => Promise<void>;
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export const EditableFeatureSections = ({ sections, onSave, isVisible, onVisibilityChange }: EditableFeatureSectionsProps) => {
  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editedSections, setEditedSections] = useState<FeatureSection[]>(sections);
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedSections.filter(s => s.headline));
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedSections(sections);
    setIsEditing(false);
  };

  const handleAddSection = () => {
    setEditedSections([...editedSections, {
      headline: '',
      bullets: ['', '', ''],
      imageUrl: ''
    }]);
  };

  const handleRemoveSection = (index: number) => {
    setEditedSections(editedSections.filter((_, i) => i !== index));
  };

  const handleUpdateSection = (index: number, field: keyof FeatureSection, value: any) => {
    const updated = [...editedSections];
    updated[index] = { ...updated[index], [field]: value };
    setEditedSections(updated);
  };

  const handleUpdateBullet = (sectionIndex: number, bulletIndex: number, value: string) => {
    const updated = [...editedSections];
    updated[sectionIndex].bullets[bulletIndex] = value;
    setEditedSections(updated);
  };

  const handleAddBullet = (sectionIndex: number) => {
    const updated = [...editedSections];
    updated[sectionIndex].bullets.push('');
    setEditedSections(updated);
  };

  const handleRemoveBullet = (sectionIndex: number, bulletIndex: number) => {
    const updated = [...editedSections];
    updated[sectionIndex].bullets = updated[sectionIndex].bullets.filter((_, i) => i !== bulletIndex);
    setEditedSections(updated);
  };

  if (isEditing) {
    return (
      <Card className="p-6 border-dashed border-2 border-primary/50 bg-primary/5">
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Edit Feature Sections</h3>
            <div className="flex gap-2">
              <Button onClick={handleAddSection} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Section
              </Button>
              <Button onClick={handleSave} size="sm" disabled={isSaving}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button onClick={handleCancel} size="sm" variant="outline">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-background rounded-lg border">
            <div className="space-y-1">
              <label className="text-sm font-medium">Visible to Public</label>
              <p className="text-xs text-muted-foreground">Show these sections on the product page</p>
            </div>
            <Switch
              checked={isVisible}
              onCheckedChange={onVisibilityChange}
            />
          </div>

          {editedSections.map((section, sectionIndex) => (
            <Card key={sectionIndex} className="p-4 space-y-4 bg-background">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Section {sectionIndex + 1}</span>
                </div>
                <Button
                  onClick={() => handleRemoveSection(sectionIndex)}
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Headline</Label>
                  <Input
                    placeholder="Feature headline"
                    value={section.headline}
                    onChange={(e) => handleUpdateSection(sectionIndex, 'headline', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    type="text"
                    placeholder="https://example.com/image.jpg or paste from gallery"
                    value={section.imageUrl || ''}
                    onChange={(e) => handleUpdateSection(sectionIndex, 'imageUrl', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste an image URL directly or copy from the gallery above
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Bullet Points</Label>
                    <Button
                      onClick={() => handleAddBullet(sectionIndex)}
                      size="sm"
                      variant="ghost"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Bullet
                    </Button>
                  </div>
                  {section.bullets.map((bullet, bulletIndex) => (
                    <div key={bulletIndex} className="flex items-center gap-2">
                      <Input
                        placeholder={`Bullet point ${bulletIndex + 1}`}
                        value={bullet}
                        onChange={(e) => handleUpdateBullet(sectionIndex, bulletIndex, e.target.value)}
                      />
                      <Button
                        onClick={() => handleRemoveBullet(sectionIndex, bulletIndex)}
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-dashed border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/20">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center">
          <Pencil className="w-8 h-8 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Feature Sections</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {sections.length === 0 
              ? "No feature sections yet. Click below to add sections with headlines, bullet points, and images."
              : `${sections.length} section${sections.length !== 1 ? 's' : ''} configured. Click below to edit.`
            }
          </p>
        </div>
        <Button
          onClick={() => {
            setIsEditing(true);
            if (sections.length === 0) {
              // Start with one empty section
              setEditedSections([{
                headline: '',
                bullets: ['', '', ''],
                imageUrl: ''
              }]);
            }
          }}
          size="default"
          variant="default"
        >
          <Pencil className="w-4 h-4 mr-2" />
          {sections.length === 0 ? 'Add Feature Sections' : 'Edit Feature Sections'}
        </Button>
      </div>
    </Card>
  );
};
